import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getApplicationData as applicationData } from 'selectors/getApplicationData';
import { FlowComponentType } from 'routes/types';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { RootState } from 'handlers';
import { createDocuSignEnvelope } from 'thunks';
import { DocuSignDocument } from 'api/DocuSignEnvelopeApi';
import FormNavigation from 'components/FormNavigation';
import { FormNavigationProps } from 'components/FormNavigation/FormNavigation';
import FormContainer from 'components/LoanForm/FormContainer';
import Button from 'components/Button';
import Loader from 'components/Loader';

import { ReactComponent as SignIcon } from 'images/sign-icon.svg';

import styles from './Authorization.module.scss';

interface AuthorizationProps extends FlowComponentType {
  NavigationComponent?: React.ComponentType<FormNavigationProps>;
  title?: string;
  description?: React.ReactNode;
  buttonText?: string;
  onBackClick?: () => void;
  returnPath?: string;
}

const Authorization = ({
  navigationInfo,
  handleNext,
  onBackClick,
  description: descriptionFromProps,
  NavigationComponent = FormNavigation,
  title = 'Authorization',
  buttonText = 'Sign Authorization',
  returnPath,
}: AuthorizationProps) => {
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { application, isLoading: isLoadingApplication } = useSelector(applicationData);
  if (!application) {
    throw new Error('Missing application data');
  }

  const { hasSignedACHForms, bankName } = application;
  const description = descriptionFromProps || (
    <p>We need to receive your authorization before we can transfer money from your {bankName} account.</p>
  );

  const { isLoading } = useSelector((state: RootState) => state.docuSignEnvelope);

  useEffect(() => {
    if (!isLoadingApplication && hasSignedACHForms) {
      handleNext();
    }
  }, [isLoadingApplication, application]);

  const handleCreateDocuSignEnvelopment = async () => {
    if (application) {
      const applicationId = application.id;
      window.location.replace(
        await dispatchWithUnwrap(
          createDocuSignEnvelope({
            applicationId,
            windowLocationHref: window.location.href,
            document: DocuSignDocument.ACH,
            returnPath,
          }),
        ),
      );
    }
  };

  return (
    <div>
      <NavigationComponent {...navigationInfo} onBackClick={onBackClick} />
      <FormContainer
        title={title}
        icon={<SignIcon />}
        subtitle={
          <div className={styles.subtitleContainer}>
            {isLoadingApplication ? <Loader color="#9d86f9" size={25} /> : description}
            <p>Click "Sign Authorization" below to be redirected to DocuSign.</p>
          </div>
        }
      >
        <Button onClick={handleCreateDocuSignEnvelopment} isLoading={isLoading}>
          {buttonText}
        </Button>
      </FormContainer>
    </div>
  );
};

export default Authorization;
